/*
 * Footer scene — scroll behaviour for the layered floral band (see
 * assets/fluevog-footer-scene.css for how the planes are composed).
 *
 * Three jobs:
 *
 * 1. Reveal. The first time the band intersects the viewport it gets
 *    .is-visible, which hands the staggered fade-in over to CSS. Never removed,
 *    so the entrance plays once per page view.
 *
 * 2. Parallax. Each plane declares a data-depth as a percentage of the band's
 *    height. Progress runs 0 (the band's top edge has just reached the bottom of
 *    the viewport) to 1 (the whole band is in view), and every plane is pushed
 *    down by (1 - progress) x depth. Nearer planes have a larger depth, so they
 *    travel further and the scene resolves into its authored composite exactly
 *    when the reader lands at the bottom of the page.
 *
 *    Planes are only ever pushed *down* here: the band is flush with the bottom
 *    of the page, so lifting one would open a gap under the grass.
 *
 * 3. Cursor follow, on pointer devices only. The cursor's position in the
 *    viewport maps to -1..1 on each axis and each plane shifts *against* it by
 *    its own data-pointer-x / data-pointer-y, the way near objects slide the
 *    other way when you move your head. The easing is a CSS transition on the
 *    plane, so this only ever writes a target and never runs its own loop.
 */
class FluevogFooterScene extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.layers = Array.from(this.querySelectorAll('[data-depth]'));
    this.pointerPlanes = Array.from(this.querySelectorAll('[data-pointer-x]'));
    this.intensity = parseFloat(this.dataset.intensity ?? '1');
    const canMove = !this.reducedMotion && this.intensity > 0;

    this.parallaxEnabled = this.dataset.parallax === 'true' && canMove && this.layers.length > 0;
    this.pointerEnabled =
      this.dataset.pointer === 'true' &&
      canMove &&
      this.pointerPlanes.length > 0 &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.visible = false;
    this.ticking = false;
    this.pointerTicking = false;
    this.pointer = { x: 0, y: 0 };
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.writePointer = this.writePointer.bind(this);

    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        if (this.visible) {
          this.classList.add('is-visible');
          this.onScroll();
        }
      },
      { rootMargin: '10% 0px' }
    );
    this.observer.observe(this);

    if (this.parallaxEnabled) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    if (this.pointerEnabled) {
      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
      document.addEventListener('pointerleave', this.onPointerLeave);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerleave', this.onPointerLeave);
    this.observer?.disconnect();
  }

  onScroll() {
    if (this.ticking || !this.parallaxEnabled) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  }

  update() {
    this.ticking = false;
    if (!this.visible) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const rect = this.getBoundingClientRect();
    if (rect.height === 0) return;

    const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / rect.height));
    const remaining = (1 - progress) * this.intensity;

    this.layers.forEach((layer) => {
      const depth = parseFloat(layer.dataset.depth || '0');
      const offset = (remaining * depth * rect.height) / 100;
      layer.style.setProperty('--fv-scene-parallax', `${offset.toFixed(2)}px`);
    });
  }

  onPointerMove(event) {
    if (event.pointerType === 'touch') return;

    const width = window.innerWidth || 1;
    const height = window.innerHeight || 1;
    this.pointer.x = (event.clientX / width) * 2 - 1;
    this.pointer.y = (event.clientY / height) * 2 - 1;

    if (this.pointerTicking || !this.visible) return;
    this.pointerTicking = true;
    requestAnimationFrame(this.writePointer);
  }

  onPointerLeave() {
    this.pointer.x = 0;
    this.pointer.y = 0;
    this.writePointer();
  }

  writePointer() {
    this.pointerTicking = false;
    const rect = this.getBoundingClientRect();
    if (rect.height === 0) return;

    this.pointerPlanes.forEach((plane) => {
      const amplitudeX = parseFloat(plane.dataset.pointerX || '0');
      const amplitudeY = parseFloat(plane.dataset.pointerY || '0');
      const x = (-this.pointer.x * amplitudeX * rect.width * this.intensity) / 100;
      const y = (-this.pointer.y * amplitudeY * rect.height * this.intensity) / 100;
      plane.style.setProperty('--fv-scene-pointer-x', `${x.toFixed(2)}px`);
      plane.style.setProperty('--fv-scene-pointer-y', `${y.toFixed(2)}px`);
    });
  }
}

if (!customElements.get('fv-footer-scene')) {
  customElements.define('fv-footer-scene', FluevogFooterScene);
}
